<template>
  <main class="kyc-address-page">
    <AppNavBar
      :title="t('card.fillInfo')"
      :border="false"
      background-color="#ffffff"
    />

    <van-skeleton
      v-if="cardKycStore.loading"
      title
      :row="8"
      class="loading-card"
    />

    <template v-else>
      <section class="form-section">
        <label class="field-label">{{ t("card.countryCode") }}</label>
        <van-field
          :model-value="cardKycStore.form.phoneCode"
          readonly
          clickable
          class="plain-field"
          :placeholder="t('card.selectCountryCode')"
          @click="openPicker('phone')"
        >
          <template #right-icon>
            <van-icon name="arrow-down" />
          </template>
        </van-field>

        <label class="field-label">{{ t("card.phoneNumber") }}</label>
        <van-field
          :model-value="cardKycStore.form.phoneNumber"
          type="tel"
          clearable
          class="plain-field"
          :placeholder="t('card.enterPhone')"
          @update:model-value="updateField('phoneNumber', $event)"
        />

        <label class="field-label">{{ t("card.address") }}</label>
        <p class="address-document-tip">
          {{ t("card.physicalAddressDocumentTip") }}
        </p>
        <van-field
          :model-value="countryName"
          readonly
          clickable
          class="plain-field"
          :placeholder="t('card.selectCountry')"
          @click="openPicker('country')"
        >
          <template #right-icon>
            <van-icon name="arrow-down" />
          </template>
        </van-field>
        <van-field
          :model-value="provinceName"
          readonly
          clickable
          class="plain-field plain-field--tight"
          :placeholder="t('card.selectRegion')"
          @click="openPicker('province')"
        >
          <template #right-icon>
            <van-icon name="arrow-down" />
          </template>
        </van-field>
        <van-field
          :model-value="cityName"
          readonly
          clickable
          class="plain-field plain-field--tight"
          :placeholder="t('card.selectCity')"
          @click="openPicker('city')"
        >
          <template #right-icon>
            <van-icon name="arrow-down" />
          </template>
        </van-field>
        <van-field
          :model-value="cardKycStore.form.addressLine1"
          rows="4"
          autosize
          type="textarea"
          clearable
          class="plain-field plain-field--tight"
          :placeholder="t('card.enterAddress')"
          @update:model-value="updateField('addressLine1', $event)"
        />

        <label class="field-label">{{ t("card.postalCode") }}</label>
        <van-field
          :model-value="cardKycStore.form.postalCode"
          clearable
          class="plain-field"
          :placeholder="t('card.enterPostalCode')"
          @update:model-value="updateField('postalCode', $event)"
        />
      </section>

      <label class="field-label">{{ t("auth.emailAddress") }}</label>
      <van-field
        :model-value="cardKycStore.form.email"
        type="text"
        clearable
        class="plain-field"
        :placeholder="t('auth.enterEmailAddress')"
        @update:model-value="updateField('email', $event)"
      />
      <van-field
        :model-value="cardKycStore.form.code"
        type="text"
        clearable
        class="plain-field"
        :style="{ marginBottom: countdown > 0 ? '2px' : '16px' }"
        :placeholder="t('auth.enterEmailCode')"
        @update:model-value="updateField('code', $event)"
      >
        <template #button>
          <button
            type="button"
            class="auth-code-btn"
            :disabled="countdown > 0"
            @click="sendCode"
          >
            {{
              countdown > 0
                ? `${t("auth.resend")}${countdown}s`
                : t("auth.getCode")
            }}
          </button>
        </template>
      </van-field>
      <label v-if="countdown > 0" class="code-send"
        >*验证码已发送，有效期10分钟</label
      >

      <section
        class="camera-permission-card"
        :class="`camera-permission-card--${cameraPermissionState}`"
      >
        <div class="camera-permission-card__content">
          <div data-strong>{{ t("card.cameraPermissionTitle") }}</div>
          <p>{{ cameraPermissionText }}</p>
        </div>
        <van-switch
          :model-value="cameraPermissionChecked"
          :loading="requestingCameraPermission"
          size="22px"
          active-color="#2c6bff"
          inactive-color="#bdbdbd"
          @update:model-value="handleCameraPermissionToggle"
        />
      </section>

      <div class="action-bar">
        <van-button
          block
          round
          type="primary"
          class="submit-btn"
          :loading="cardKycStore.submitting"
          @click="submitKyc"
        >
          {{ t("common.continue") }}
        </van-button>
      </div>
    </template>

    <CountryCodePicker
      v-model="countryCodeVisible"
      :selected-code="cardKycStore.form.phoneCode"
      @select="selectCountryCode"
    />

    <AreaSelectorPopup
      v-model="pickerVisible"
      :title="pickerTitle"
      :items="pickerItems"
      :mode="pickerMode"
      @select="selectAction"
    />
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { showFailToast } from "vant";
import { showPassedToast } from "@/utils/toast";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import AreaSelectorPopup from "@/view/card/components/AreaSelectorPopup.vue";
import CountryCodePicker from "@/view/card/components/CountryCodePicker.vue";
import { resolveDiditVerificationUrl } from "@/view/card/utils";
import { getLocalizedAreaName } from "@/utils/display-name";
import { emailSend } from "@/api/auth";
import { useCardKycStore } from "@/stores/card-kyc";
import { useProfileStore } from "@/stores/profile";
import { getDeviceId } from "@/utils/device";
import { navigateWithState } from "@/utils/route-state";
import {
  isCameraPermissionDeniedError,
  isCameraSecureContext,
  mapCameraErrorMessage,
  resolveCameraGuideType,
  showCameraGuide,
} from "@/utils/camera";

const { t, locale } = useI18n();
const router = useRouter();
const profileStore = useProfileStore();
const cardKycStore = useCardKycStore();

const pickerType = ref("");
const pickerVisible = ref(false);
const countryCodeVisible = ref(false);
const countdown = ref(0);
const cameraPermissionState = ref("unknown");
const requestingCameraPermission = ref(false);
let timer = null;
let cameraPermissionStatus = null;
let cameraPermissionVerified = false;

const pickerTitle = computed(() => {
  if (pickerType.value === "country") return t("card.selectCountry");
  if (pickerType.value === "province") return t("card.selectRegion");
  return t("card.selectCity");
});

const pickerMode = computed(() =>
  pickerType.value === "country" ? "country" : "area",
);

const pickerItems = computed(() => {
  if (pickerType.value === "country") return cardKycStore.countries;
  if (pickerType.value === "province") return cardKycStore.provinces;
  return cardKycStore.cities;
});

const cameraPermissionText = computed(() => {
  if (cameraPermissionState.value === "granted") {
    return t("card.cameraPermissionGranted");
  }
  if (cameraPermissionState.value === "denied") {
    return t("card.cameraPermissionDenied");
  }
  return t("card.cameraPermissionTip");
});

const countryName = computed(
  () => getLocalizedAreaName(cardKycStore.selectedCountry, locale.value) || "",
);
const provinceName = computed(
  () => getLocalizedAreaName(cardKycStore.selectedProvince, locale.value) || "",
);
const cityName = computed(
  () => getLocalizedAreaName(cardKycStore.selectedCity, locale.value) || "",
);

const cameraPermissionChecked = computed(
  () => cameraPermissionState.value === "granted",
);

function startCountdown() {
  countdown.value = 60;
  window.clearInterval(timer);
  timer = window.setInterval(() => {
    countdown.value -= 1;
    if (countdown.value <= 0) {
      window.clearInterval(timer);
      timer = null;
    }
  }, 1000);
}

async function sendCode() {
  if (!String(cardKycStore.form.email || "").trim()) {
    showFailToast(t("auth.enterEmailAddress"));
    return;
  }

  const res = await emailSend({
    token: getDeviceId(),
    email: cardKycStore.form.email,
    type: "KYC",
  });
  if (res) {
    startCountdown();
    showPassedToast(t("auth.codeSent"));
  }
}

async function syncCameraPermissionState() {
  if (!navigator.permissions?.query) {
    return;
  }

  try {
    if (!cameraPermissionStatus) {
      cameraPermissionStatus = await navigator.permissions.query({
        name: "camera",
      });
      cameraPermissionStatus.onchange = () => {
        updateCameraPermissionState(cameraPermissionStatus?.state);
      };
    }
    updateCameraPermissionState(cameraPermissionStatus.state);
  } catch (error) {
    // ignore browser compatibility issues
  }
}

function updateCameraPermissionState(state) {
  const nextState = state || "unknown";

  if (nextState === "granted") {
    cameraPermissionVerified = true;
    cameraPermissionState.value = "granted";
    return;
  }

  if (nextState === "denied") {
    cameraPermissionVerified = false;
    cameraPermissionState.value = "denied";
    return;
  }

  // Some Android browsers keep reporting "prompt" after getUserMedia succeeds.
  // Keep the switch on until access is explicitly denied or the page is reloaded.
  if (!cameraPermissionVerified) {
    cameraPermissionState.value = nextState;
  }
}

function releaseMediaStream(stream) {
  stream?.getTracks?.().forEach((track) => track.stop());
}

function handleVisibilityChange() {
  if (document.visibilityState === "visible") {
    syncCameraPermissionState();
  }
}

async function requestCameraPermission() {
  if (!isCameraSecureContext()) {
    showFailToast(t("wallet.cameraSecureContextRequired"));
    await showCameraGuide("secure");
    return false;
  }

  if (!navigator.mediaDevices?.getUserMedia) {
    showFailToast(t("wallet.cameraUnavailable"));
    await showCameraGuide("unavailable");
    return false;
  }

  requestingCameraPermission.value = true;
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: "user" },
      audio: false,
    });
    releaseMediaStream(stream);
    cameraPermissionVerified = true;
    cameraPermissionState.value = "granted";
    showPassedToast(t("card.cameraPermissionGrantedToast"));
    return true;
  } catch (error) {
    cameraPermissionVerified = false;
    cameraPermissionState.value = isCameraPermissionDeniedError(error)
      ? "denied"
      : "unknown";
    showFailToast(mapCameraErrorMessage(error));
    await showCameraGuide(resolveCameraGuideType(error, "unavailable"));
    return false;
  } finally {
    await syncCameraPermissionState();
    requestingCameraPermission.value = false;
  }
}

async function handleCameraPermissionToggle(value) {
  if (!value) {
    if (cameraPermissionState.value === "granted") {
      showPassedToast(t("card.cameraPermissionGrantedToast"));
    }
    return;
  }

  if (cameraPermissionState.value === "granted") {
    showPassedToast(t("card.cameraPermissionGrantedToast"));
    return;
  }

  await requestCameraPermission();
}

function updateField(field, value) {
  cardKycStore.form[field] = value;
}

async function openPicker(type) {
  if (type === "phone") {
    countryCodeVisible.value = true;
    return;
  }
  pickerType.value = type;
  if (type === "country" && !cardKycStore.countries.length) {
    await cardKycStore.ensureCountries();
  }
  if (type === "province" && !cardKycStore.form.countryId) {
    showFailToast(t("card.selectCountry"));
    return;
  }
  if (type === "city" && !cardKycStore.form.provinceId) {
    showFailToast(t("card.selectRegion"));
    return;
  }
  pickerVisible.value = true;
}

async function selectCountryCode(item) {
  const index = cardKycStore.getCountryCodeIndex(item?.dial_code);
  if (index >= 0) {
    await cardKycStore.selectPhoneCode(index);
  }
}

async function selectAction({ index }) {
  if (pickerType.value === "country") {
    await cardKycStore.selectCountry(index);
    return;
  }
  if (pickerType.value === "province") {
    await cardKycStore.selectProvince(index);
    return;
  }
  cardKycStore.selectCity(index);
}

async function submitKyc() {
  const validateKey = cardKycStore.validate();
  if (validateKey) {
    showFailToast(t(validateKey));
    return;
  }

  if (!profileStore.platformAddress) {
    router.replace("/login");
    return;
  }

  const hasCameraPermission =
    cameraPermissionState.value === "granted"
      ? true
      : await requestCameraPermission();
  if (!hasCameraPermission) {
    return;
  }

  const result = await cardKycStore.submit({
    address: profileStore.platformAddress,
  });
  // const result =  { token: 'm9WR6xXeLOPC' }
  const verificationToken = String(
    result?.token || cardKycStore.verificationToken || "",
  ).trim();
  const verificationUrl = resolveDiditVerificationUrl({
    url: result?.url || cardKycStore.verificationUrl || "",
    token: verificationToken,
  });

  if (!verificationUrl) {
    showFailToast(t("card.verificationPageUnavailable"));
    return;
  }

  navigateWithState(router, "/card/kyc/webview", {
    namespace: "card-kyc-webview",
    replace: true,
    payload: {
      url: verificationUrl,
      token: verificationToken,
    },
  });
}

onMounted(async () => {
  if (!profileStore.platformAddress) {
    router.replace("/login");
    return;
  }
  document.addEventListener("visibilitychange", handleVisibilityChange);
  await syncCameraPermissionState();
  await cardKycStore.init({ address: profileStore.platformAddress });
});

onBeforeUnmount(() => {
  document.removeEventListener("visibilitychange", handleVisibilityChange);
  if (cameraPermissionStatus) {
    cameraPermissionStatus.onchange = null;
    cameraPermissionStatus = null;
  }
  if (timer) {
    window.clearInterval(timer);
    timer = null;
  }
});
</script>

<style scoped lang="scss">
.kyc-address-page {
  min-height: var(--app-viewport-height);
  padding: 14px 16px 36px;
  background: #ffffff;
}

.loading-card {
  margin-top: 4px;
}

.form-section {
  padding-top: 2px;
}

.field-label {
  display: block;
  margin: 0 0 10px;
  color: #1f2430;
  font-size: 15px;
  font-weight: 500;
}

.code-send {
  font-size: 12px;
  color: red;
  display: block;
  margin: 0 0 16px;
  font-weight: 500;
}

.plain-field {
  margin-bottom: 16px;
  border-radius: 12px;
  overflow: hidden;

  &--tight {
    margin-top: -6px;
  }

  :deep(.van-cell) {
    padding: 14px;
    background: #f4f5f6;
  }

  :deep(.van-field__control),
  :deep(.van-field__control::placeholder) {
    font-size: 14px;
  }

  :deep(textarea.van-field__control) {
    min-height: 94px !important;
  }
}

.field-arrow {
  display: block;
  width: 16px;
  height: 16px;
}

.action-bar {
  padding-top: 12px;
}

.address-document-tip {
  margin: 10px 0 10px;
  color: #999999;
  font-size: 12px;
  color: var(--wp-color-theme);
  line-height: 18px;
}

.camera-permission-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 2px;
  padding: 14px 14px 13px;
  border-radius: 12px;
  background: #eef4ff;
}

.camera-permission-card__content {
  flex: 1;
  min-width: 0;

  [data-strong] {
    display: block;
    margin-bottom: 8px;
    color: #000000;
    font-size: 14px;
    font-weight: 500;
  }

  p {
    margin: 0;
    color: #666666;
    font-size: 12px;
    line-height: 18px;
  }
}

.submit-btn {
  box-shadow: 0 8px 16px rgba(44, 107, 255, 0.22);
}

.auth-code-btn {
  padding: 7px 9px;
  border: 0;
  border-radius: 6px;
  background: #d5e5ff;
  color: #2667ff;
  font-size: 12px;
  line-height: 1;
  white-space: nowrap;
}
</style>
